import { defineStore } from 'pinia';
import { clearLegacyToken, logout as httpLogout } from '@erebus/http';
import { api } from '../boot/axios';
import { isCookieAuthMode } from '../boot/authMode';
import {
  clearLogoutPending,
  confirmLogout,
  isLogoutPending,
  markLogoutPending,
} from '../boot/logoutPending';
import { useMemberStore } from './member';
import { useApplicationStore } from './application';
import { useWorkflowStore } from './workflow';
import { AuthRoles } from '../logic/auth';
import { useNotificationStore } from 'stores/notification';
import { useCookieLoginStore } from './cookieLogin';

/** State that is never written to storage. */
const NOT_PERSISTED = [
  // The plaintext password, held only while the change-password page needs it.
  'temporaryPassword',
  // Cookie auth: the session lives in an HttpOnly cookie, so a saved flag would
  // claim a session the browser may no longer have.
  'sessionActive',
];

/**
 * The state written to storage. Read when the store is first created, which is
 * after boot has picked the auth mode. Cookie mode never saves `token`: the
 * session lives in the cookie.
 */
const persistedPaths = () =>
  Object.keys(authState()).filter(
    (key) => !NOT_PERSISTED.includes(key) && !(isCookieAuthMode() && key === 'token'),
  );

/**
 * How long a cookie-mode logout waits for the device DELETE before it ends the
 * Go session anyway. Short, because the tab may be closed straight after.
 */
export const LOGOUT_DEVICE_DELETE_CAP_MS = 3000;

/**
 * The server side of a cookie-mode logout while it runs, else null. A second
 * logout does not start it again, and a new login waits for it: run late, its
 * `/auth/logout` would carry the new session's cookie and end that instead.
 */
let cookieLogoutPending = null;

const authState = () => ({
  clientAndLocationNumber: '',
  clientDoctorPhoneNumber: '',
  employeeId: localStorage.getItem('employee-id') || 0,
  clientId: localStorage.getItem('client-id') || 0,
  authRoles: {},
  clientBrandName: 'Praxes',
  hasLoadedOnce: false,
  isClientAdmin: false,
  isDoctor: false,
  isLocationAdmin: false,
  isMedicalAdmin: false,
  isMedicalDirector: false,
  isQualityManager: false,
  isReferralDoctor: false,
  isOhCaseManager: false,
  locale: 'en',
  outStandingTrainingFlag: false,
  redirect: '', // the path to take the user to after they log in
  requiresAgreement: false,
  requiresPasswordChange: false,
  requiresEmployeeCell: false,
  requiresSecondFactorAuth: false,
  requiresQuickStart: false,
  showCovid: false,
  showFirstAid: false,
  showMCP: false,
  showOhClinic: false,
  showMessaging: false,
  showMedKit: false,
  showWorkflows: false,
  showVideo: false,
  signedOutTime: 0,
  sbmToken: '',
  status: '',
  timezone: 'America/Halifax',
  token: localStorage.getItem('user-token') || '',
  user: {},
  dialogDisplayed: false,
  // SECURITY: Temporary password for password change flow - in-memory only, NOT persisted
  // Cleared on logout and after use to prevent exposure
  temporaryPassword: '',
  // Cookie auth only: the session was restored or a login succeeded. In memory only.
  sessionActive: false,
});

export const useAuthStore = defineStore('auth', {
  persist: {
    get paths() {
      return persistedPaths();
    },
  },

  state: authState,

  getters: {
    isAuthenticated: (state) => {
      if (isCookieAuthMode()) {
        return state.sessionActive;
      }
      return state && state.token && state.token !== 'null' && state.token !== 'undefined';
    },
    authStatus: (state) => state.status,
    getClientId: (state) => {
      return state.clientId;
    },
    getIsPraxes: (state) => {
      return state.clientId === 1;
    },
    getUser: (state) => {
      const signedIn = isCookieAuthMode()
        ? state.sessionActive
        : state && state.token && state.token !== 'null' && state.token !== 'undefined';
      if (signedIn) {
        return state.user;
      } else {
        return {};
      }
    },
    isSbmOnline: (state) => state.sbmToken && state.sbmToken.length > 0,
    getClientName: (state) => {
      return state.user.clientName;
    },
    hasRoles: (state) => (roles) => {
      const authRoles = new AuthRoles(state.authRoles);
      return authRoles.hasRoles(roles);
    },
    hasAnyRole: (state) => (roles, clientID, locationID) => {
      const authRoles = new AuthRoles(state.authRoles);
      return authRoles.hasAnyRole(roles, clientID, locationID);
    },
    hasGlobalRole: (state) => (role) => {
      const authRoles = new AuthRoles(state.authRoles);
      return authRoles.hasGlobalRole(role);
    },
    hasGlobalRoles: (state) => (roles) => {
      const authRoles = new AuthRoles(state.authRoles);
      return authRoles.hasGlobalRoles(roles);
    },
    /**
     * Checks if a temporary password is stored for password change flow
     * @returns {boolean} True if temporary password exists
     */
    hasTemporaryPassword: (state) => {
      return state.temporaryPassword && state.temporaryPassword.length > 0;
    },
  },

  actions: {
    /**
     * Stores the login state from a legacy response: login, Agree,
     * ChangePassword and QuickStart all answer with it.
     *
     * In cookie mode it hands off to authSessionSuccess, so no caller can save
     * the Java token those responses carry.
     *
     * @param {Object} resp - Legacy's login state, with its access token
     */
    authSuccess(resp) {
      if (isCookieAuthMode()) {
        this.authSessionSuccess(resp);
        return;
      }
      // saving token
      this.token = resp.accessToken;
      this.requiresSecondFactorAuth = resp.requiresSecondFactorAuth;
      this.applyLoginState(resp);
    },
    /**
     * authSuccess for cookie auth. It stores no token: the session cookie is the
     * credential, and `@erebus/http` holds the Java token in memory.
     *
     * The second factor and passwords are Go's job in cookie mode, so legacy's
     * 2FA, phone and password-change gates are cleared rather than copied.
     * Left set, they would send the user to legacy pages that cookie mode does
     * not use.
     *
     * Nothing is written until the body is known to be usable, and the user is
     * marked signed in last. A body it cannot use throws first, so the caller
     * takes its failure path with no half-filled, "signed in" store.
     *
     * @param {Object} resp - The login state, from GET /api/v1/authentication/session
     * @throws {Error} When the body is not a usable login state
     */
    authSessionSuccess(resp) {
      if (typeof resp !== 'object' || resp === null || Array.isArray(resp)) {
        throw new Error('Unusable session state');
      }
      // Legacy sends null when the client's ClientModules column is NULL
      // (PraxesLogic loadClientSecurity): no modules enabled. Any other
      // non-string is a body this cannot read. `modules` is the one field
      // applyLoginState reads into (indexOf).
      const modules = resp.modules ?? '';
      if (typeof modules !== 'string') {
        throw new Error('Unusable session state');
      }
      this.requiresSecondFactorAuth = false;
      this.requiresEmployeeCell = false;
      this.applyLoginState({ ...resp, modules });
      // Go owns passwords in cookie mode: its must_reset page is the only
      // forced reset, so legacy's flag (and /login/change) is ignored.
      this.requiresPasswordChange = false;
      this.sessionActive = true;
    },
    /** Copies the login state that both auth modes share. Never the token. */
    applyLoginState(resp) {
      this.status = 'success';
      this.employeeId = resp.employeeID;
      this.clientId = resp.clientID;
      this.clientBrandName = resp.clientBrandName;
      this.locale = resp.locale;
      this.timezone = resp.timezone;
      this.outStandingTrainingFlag = resp.outStandingTrainingFlag;
      this.requiresAgreement = resp.requiresAgreement;
      this.requiresPasswordChange = resp.requiresPasswordChange;
      this.requiresQuickStart = resp.requiresQuickStart;
      this.authRoles = resp.authRoles;
      this.isClientAdmin = resp.clientAdmin;
      this.isDoctor = resp.doctor;
      this.isLocationAdmin = resp.locationAdmin || resp.clientAdmin;
      this.isMedicalAdmin = resp.medicalAdmin;
      this.isMedicalDirector = resp.medicalDirector;
      this.isQualityManager = resp.qualityManager;
      this.isReferralDoctor = resp.referralDoctor;
      this.isOhCaseManager = resp.ohCaseManager;
      this.clientDoctorPhoneNumber = resp.clientDoctorPhoneNumber;
      this.clientAndLocationNumber = resp.clientAndLocationNumber;
      this.showCovid = resp.modules.indexOf(',covid19,') >= 0;
      this.showFirstAid = resp.modules.indexOf(',firstaid,') >= 0;
      this.showMCP = resp.modules.indexOf(',mcp,') >= 0;
      this.showOhClinic = resp.modules.indexOf(',ohclinic,') >= 0;
      this.showMessaging = resp.modules.indexOf(',mcpmessaging,') >= 0;
      this.showMedKit = resp.modules.indexOf(',medkit,') >= 0;
      this.showWorkflows = resp.modules.indexOf(',workflows,') >= 0;
      this.showVideo = resp.modules.indexOf(',videoconference,') >= 0;
    },
    authError() {
      this.status = 'error';
      this.hasLoadedOnce = false;
    },
    authHasLoaded() {
      this.hasLoadedOnce = true;
    },
    authIntakeSuccess(client) {
      this.clientId = client.clientID;
      this.clientAndLocationNumber = client.clientAndLocationNumber;
    },

    /**
     * Cookie auth: loads the login state for the current session and marks the
     * user signed in. Legacy answers with the same fields as login, minus the
     * token. Rejects on a 401 (no session) or a server error.
     *
     * @returns {Promise<Record<string, any>>} The session state
     */
    async loadSessionState() {
      const { data } = await api.get('/api/v1/authentication/session');
      this.authSessionSuccess(data);
      return data;
    },

    authRequest(credentials) {
      return new Promise((resolve, reject) => {
        this.status = 'loading';
        this.token = '';
        api
          .post('/api/v1/authentication', credentials)
          .then((response) => {
            this.processLogin(response);
            resolve(response.data);
          })
          .catch((err) => {
            this.status = 'error';
            this.hasLoadedOnce = false;
            reject(err);
          });
      });
    },
    authEmployeeCell(updatephone) {
      updatephone.employeeID = this.employeeId;
      return new Promise((resolve) => {
        api.post('/api/v1/authentication/updatephone', updatephone).then((response) => {
          this.processLogin(response);
          resolve(response.data);
        });
      });
    },
    authSecond(data) {
      return new Promise((resolve) => {
        api.post('/api/v1/authentication/secondfactor', data).then((response) => {
          this.processLogin(response);
          resolve(response.data);
        });
      });
    },
    async sendEmailCode(employeeID) {
      return await api.get(`/api/v1/authentication/email/${employeeID}`);
    },
    async updateEmployeeEmail(email) {
      return await api.post(`/api/v1/authentication/email`, { email });
    },
    authCompleteQuickStart() {
      return new Promise((resolve) => {
        api
          .post('/api/v1/authentication/completequickstart', {
            quickStartFlag: 1,
          })
          .then((response) => {
            this.processLogin(response);
            resolve(response.data);
          });
      });
    },
    async processLogin(response) {
      // Cookie mode: Go owns the second factor, so legacy's phone and 2FA gates
      // do not apply (see authSessionSuccess).
      const legacyGates = !isCookieAuthMode();
      if (legacyGates && response.data.requiresEmployeeCell) {
        this.status = 'employeecell';
        this.token = '';
        this.employeeId = response.data.employeeID;
        this.requiresEmployeeCell = response.data.requiresEmployeeCell;
      } else if (legacyGates && response.data.requiresSecondFactorAuth) {
        this.status = '2fa';
        // A wrong code comes back with no token (PRX-2351). Keep the one we have,
        // or "email me the code" would send no credentials.
        if (response.data.accessToken) {
          this.token = response.data.accessToken;
        }
        this.employeeId = response.data.employeeID;
        this.requiresEmployeeCell = response.data.requiresEmployeeCell;
        this.requiresSecondFactorAuth = response.data.requiresSecondFactorAuth;
      } else {
        // Cookie auth keeps no token in storage. Any token in the response is
        // ignored: `@erebus/http` holds its own Java token in memory.
        if (!isCookieAuthMode()) {
          const token = response.data.accessToken;
          localStorage.setItem('user-token', token);
          localStorage.setItem('employee-id', response.data.employeeID);
          localStorage.setItem('client-id', response.data.clientID);
        }

        this.startLoginNotifications();
        this.authSuccess(response.data);
        await this.loadAfterLogin(response.data);
      }
    },
    /** Asks for push permission after a login. Never fails the login. */
    startLoginNotifications() {
      try {
        const notificationStore = useNotificationStore();
        if (notificationStore) {
          notificationStore.handleLogin().catch((e) => {
            console.warn('Notification handling failed but continuing login:', e);
          });
        }
      } catch (e) {
        console.error('Error in notification handling:', e);
        // Continue with login process regardless of notification error
      }
    },
    /**
     * Downloads the user's data after a login: the user, the app data and the
     * workflows. Once per session, and never for a referral doctor.
     *
     * @param {Object} data - The login state, for its generic-medications setting
     */
    async loadAfterLogin(data) {
      // Download data in the background.
      if (!this.hasLoadedOnce && !this.isReferralDoctor) {
        const memberStore = useMemberStore();
        await memberStore.fetchUser();
        const appStore = useApplicationStore();

        appStore.useGenericMeds = data.useGenericMedications;
        await appStore.dataRefresh();
        const workflowStore = useWorkflowStore();
        if (this.showWorkflows) {
          try {
            workflowStore.fetchTemplates();
            workflowStore.fetchProtocolMaps();
          } catch {
            // Ignore
          }
        }
        this.hasLoadedOnce = true;
      }
    },
    /**
     * Cookie auth: finishes a Go login or MFA verify. The session cookie and
     * the Java token are already in place, so this loads the login state from
     * legacy and then starts the same after-login work as a bearer login.
     *
     * Resolves once the user is signed in. The data download runs behind it,
     * as it does for a bearer login, where authRequest does not wait for it.
     *
     * If `/session` fails, Go's session is ended before this rejects. Go has
     * already set the cookie, and leaving it behind a "login failed" message
     * would let the next reload sign in as this user.
     *
     * @returns {Promise<Record<string, any>>} The session state
     */
    async completeCookieLogin() {
      let data;
      try {
        data = await this.loadSessionState();
      } catch (err) {
        if (!(await confirmLogout(httpLogout))) {
          console.warn('Could not end the session on the server');
          markLogoutPending();
        }
        throw err;
      }
      // The new session's cookie replaced the old one, so a logout still owed
      // for that one can no longer be sent from here.
      clearLogoutPending();
      this.startLoginNotifications();
      this.loadAfterLogin(data).catch(() => {
        console.warn('Could not load data after login');
      });
      return data;
    },
    /**
     * Signs out. The state is cleared at once and the server calls run behind
     * it, so this resolves straight away.
     *
     * Cookie mode:
     * - The Java token is dropped at once, so nothing still running can reach
     *   the Java API with it.
     * - The device DELETE is authenticated by the session cookie, so Go's
     *   logout (which drops the cookie) goes after it. It waits at most
     *   LOGOUT_DEVICE_DELETE_CAP_MS, then ends the Go session whatever the
     *   DELETE did. A slow DELETE must never leave the session alive.
     * - Safe to call twice: a second call while the server calls run does not
     *   repeat them, and a call with no active session and no logout owed
     *   makes none. Calls in flight at logout come back as 401s and land here
     *   again; `/auth/logout` is limited to 5 a minute per IP.
     */
    authLogout() {
      return new Promise((resolve) => {
        const appStore = useApplicationStore();
        const notificationStore = useNotificationStore();
        const cookieAuth = isCookieAuthMode();
        const deviceDeleted = notificationStore
          .deleteErebusToken(cookieAuth ? LOGOUT_DEVICE_DELETE_CAP_MS : undefined)
          .catch(() => {
            console.warn('Could not unregister this device for push notifications');
          });
        if (cookieAuth) {
          clearLegacyToken();
          useCookieLoginStore().clear();
        }
        const startServerLogout =
          cookieAuth && !cookieLogoutPending && (this.sessionActive || isLogoutPending());
        if (startServerLogout) {
          const capped = Promise.race([
            deviceDeleted,
            new Promise((done) => setTimeout(done, LOGOUT_DEVICE_DELETE_CAP_MS)),
          ]);
          cookieLogoutPending = capped
            .then(() => confirmLogout(httpLogout))
            .then((ended) => {
              if (!ended) console.warn('Could not end the session on the server');
            })
            .finally(() => {
              cookieLogoutPending = null;
            });
        }
        this.sessionActive = false;
        this.status = 'logged-out';
        this.token = '';
        // SECURITY: Clear temporary password on logout
        this.temporaryPassword = '';
        appStore.reset();
        // After reset, which clears storage. The next boot ends the session
        // first if /auth/logout never gets through (boot/logoutPending).
        if (startServerLogout) {
          markLogoutPending();
        }
        const authStore = useAuthStore();
        authStore.signedOutTime = new Date().getTime();
        resolve();
      });
    },
    /**
     * Cookie auth: resolves once the server side of a logout has finished, at
     * once if none is running. A login waits on this first.
     *
     * @returns {Promise<void>}
     */
    async logoutSettled() {
      await cookieLogoutPending;
    },
    /**
     * Cookie auth: makes a logout started elsewhere — the boot-time one in
     * boot/session — something a login waits for. Boot only waits 5 s for it,
     * but its request runs on. Its late answer clears the session cookie,
     * which by then could be a new login's.
     *
     * @param {Promise<unknown>} logout - The logout request, while it runs
     */
    trackLogout(logout) {
      const tracked = Promise.resolve(logout)
        .catch(() => {})
        .finally(() => {
          if (cookieLogoutPending === tracked) cookieLogoutPending = null;
        });
      cookieLogoutPending = tracked;
    },
    /**
     * SECURITY: Sets temporary password for password change flow
     * Stored in-memory only, cleared on logout and after use
     * @param {string} password - Temporary password to store
     */
    setTemporaryPassword(password) {
      this.temporaryPassword = password;
    },
    /**
     * SECURITY: Clears temporary password after use or on logout
     * Should be called immediately after password change completes
     */
    clearTemporaryPassword() {
      this.temporaryPassword = '';
    },
    async getVideoStatus(theCase) {
      try {
        const res = await api.get(
          `/api/v2/video/status?patientRecordID=${theCase.patientRecordID}`,
        );
        if (res.status !== 200) {
          return false;
        }
        return res.data;
      } catch (e) {
        console.error(e);
      }
    },
  },
});
